// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import {
  type AutodeskCloudModel,
  useEvolvePaginatedGet,
  useEvolvePost,
  type WithRequiredNotNull,
} from '@evolvemep/api-ts';
import { Loader, Text, Flex } from '@mantine/core';

import { urlEncodeUrn } from 'components/Autodesk/extensions/utils';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';

type Props = {
  modelToUnlink: Pick<AutodeskCloudModel, 'projectId' | 'urn'> | null;
  refresh: () => void;
  onClose: () => void;
};

const UnlinkModelModalText = ({ modelToUnlink }: WithRequiredNotNull<Pick<Props, 'modelToUnlink'>>) => {
  const { setDisabled } = useWrappedModal();
  const { entireCount, loading, fetchNextPage } = useEvolvePaginatedGet('shop/spool', {
    perPage: 1,
    defaultConfig: {
      params: {
        projectId: modelToUnlink.projectId,
        urn: encodeURIComponent(modelToUnlink.urn),
      },
    },
  });

  useEffect(() => {
    // Load just one page to check if we have any linked spools or not
    void fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    setDisabled(loading);
  }, [setDisabled, loading]);

  if (loading || entireCount === undefined) {
    return (
      <Flex justify="center" align="center">
        <Loader />
      </Flex>
    );
  }
  return (
    <Text>
      {entireCount === 0 ? (
        <>Are you sure you want to unlink this model from this project?</>
      ) : (
        <>
          Unlinking a model will not remove any spool data from the EVOLVE Project but will make the model no longer
          accessible through the EVOLVE Project. Are you sure you want to continue?
        </>
      )}
    </Text>
  );
};

export const UnlinkModelModal = ({ modelToUnlink, ...props }: Props) => {
  const { apiCall: unlink, loading: unlinking } = useEvolvePost('shop/autodesk/unlinkModel/:urn');
  return (
    <VariableTrackingModal
      title="Unlink model"
      variable={modelToUnlink}
      onClose={props.onClose}
      buttonColor="red"
      confirmationText="Unlink"
      loading={unlinking}
      onConfirm={async (currentModelToUnlink) => {
        await unlink(
          {},
          {
            url: `shop/autodesk/unlinkModel/${urlEncodeUrn(currentModelToUnlink.urn)}`,
          },
        );
        props.refresh();
        props.onClose();
      }}
      size="sm"
    >
      {(currentModelToUnlink) => <UnlinkModelModalText modelToUnlink={currentModelToUnlink} {...props} />}
    </VariableTrackingModal>
  );
};
